import * as React from "react";

import { formatDateTime } from "@impero-com/common/utils/formatting";
import { t } from "@impero-com/common/utils/i18n";
import { Button, DisabledLink } from "@impero-com/ui";
import { Popover } from "antd";
import memoizeOne from "memoize-one";
import moment, { Moment } from "moment-timezone";

import {
    ActivityLogForRiskNodeActivity,
    ActivityLogEventForRiskNodeActivity,
} from "api/generated/model";

import * as theme from "./theme.css";

type ActivityLogEvent<T> = Omit<
    ActivityLogEventForRiskNodeActivity,
    "operation"
> & {
    operation: T;
};

type ActivityLogDto<T> = Omit<ActivityLogForRiskNodeActivity, "events"> & {
    events: ActivityLogEvent<T>[];
};

interface ActivityLogProps<T, LR> {
    activityLog: ActivityLogDto<T>;
    linkedResources: LR;
    renderItem: (
        linkedResources: LR,
        item: T,
        userId: number,
        at: Moment
    ) => React.ReactNode;
    onDateChange: (date: Moment) => void;
    historyDate: Moment | null;
}

/*
 * Common renderer for all Activity Logs
 */
class ActivityLog<T, LR> extends React.PureComponent<ActivityLogProps<T, LR>> {
    render = () => {
        const {
            activityLog,
            linkedResources,
            renderItem,
            onDateChange,
            historyDate,
        } = this.props;
        const events = sortChanges(activityLog.events);
        return (
            <div className={theme.logHistoryContainer}>
                <div>
                    {activityLog.type === "PreHistoric" ? (
                        <WarningAboutHistoryFeature />
                    ) : null}
                    {events.map((event, index) => {
                        const date = moment(event.at);
                        return (
                            <ActivityLogItem
                                key={event.at + index}
                                date={date}
                                historyDate={historyDate}
                                onDateChange={onDateChange}
                            >
                                {renderItem(
                                    linkedResources,
                                    event.operation,
                                    event.by,
                                    date
                                )}
                            </ActivityLogItem>
                        );
                    })}
                </div>
            </div>
        );
    };
}

interface ActivityLogItemPropsBase {
    date: Moment;
    historyDate: Moment | null;
    onDateChange: (date: Moment) => void;
    children: React.ReactNode;
}

function sortEvents<T>(events: ActivityLogEvent<T>[]): ActivityLogEvent<T>[] {
    return events.sort(
        (a, b) => Number(new Date(b.at)) - Number(new Date(a.at))
    );
}

const sortChanges = memoizeOne(sortEvents);

/*
 * One item in an Activity Log
 */
class ActivityLogItem extends React.PureComponent<ActivityLogItemPropsBase> {
    render = () => {
        const { date, onDateChange, historyDate, children } = this.props;
        const formattedDate = formatDateTime(date.toDate());
        const itemMoment = moment(date);
        const itemDateMatchHistoryDate = itemMoment.isSame(historyDate);

        return (
            <div>
                {itemDateMatchHistoryDate ? (
                    <DisabledLink
                        reason={t(
                            "RiskMap_History_Drawer_JumpToDate_Same_Date"
                        )}
                    >
                        {formattedDate}
                    </DisabledLink>
                ) : (
                    <Popover
                        content={t("RiskMap_History_Drawer_JumpToDate_Tooltip")}
                    >
                        <Button
                            variant="link"
                            onClick={() => onDateChange(date)}
                            className={theme.logDateButton}
                        >
                            {formattedDate}
                        </Button>
                    </Popover>
                )}
                <div className={theme.logContainer}>{children}</div>
            </div>
        );
    };
}

const WarningAboutHistoryFeature: React.FC = () => (
    <div className={theme.logContainer}>
        {t("RiskMap_History_Drawer_PreHistoricData")}
    </div>
);

export { ActivityLog };
